import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { AlertTriangle, Clock, Eye, EyeOff, FlaskConical, Loader2, Lock, Mail, Star, Trash2, User, Users, Zap } from 'lucide-react';
import { formatDate, getRoleLabel } from '../../lib/utils';
import { AssigneeDocumentsDialog } from '../../components/AssigneeDocumentsDialog';
import { useDM } from '../../contexts/DMContext';
import { requestsApi } from '../../lib/api';
import { toast } from 'sonner';

// iter119 — privacy hardening. Only true privileged staff (NOT Coordinators) and the
// user themselves can view another user's required documents. Coordinators are intentionally
// excluded so peers cannot inspect each other's compliance docs.
const ROLES_THAT_CAN_VIEW_DOCS = ['ADMIN', 'CRD', 'CHAIRMAN', 'DIRECTOR', 'EXEC_DIRECTOR'];

export function AssignmentsTab({
  request,
  assignments,
  documents = [],
  isCompanyRep,
  isAdmin,
  isChairman,
  onRemoveAssignment,
  onToggleAutoAssign,
  currentUserRole,
  currentUserId,
  docAlerts = [],
  pendingInvitations = [],
  onRefresh,
}) {
  const [removingId, setRemovingId] = useState(null);
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  // iter167 Issue 12 — toggling viewer-only access per assignee
  const [togglingViewerId, setTogglingViewerId] = useState(null);
  // iter163j — DM hook so each assignee row can offer a one-click private chat
  // with the same /req card pre-attached as the header-level "Discuss in DM".
  const { eligible: dmEligible, startDMWith } = useDM();
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [isSelfView, setIsSelfView] = useState(false);

  // iter120 — privileged staff baseline + per-request operator escalation: the assigned
  // Coordinator (CTA/FEA) and the assigned Financial Officer (FT) are the requests's
  // active operators, so they ALSO need to view other assignees' required documents to
  // do their compliance work.
  // iter167 — Include the CRD-delegated FEA Coordinator (lives on
  // `request.fea_coordinator_user_id`, not in db.assignments). Without this,
  // the FEA Coordinator couldn't click on an assignee row to see their docs.
  const baselineCanView = ROLES_THAT_CAN_VIEW_DOCS.includes(currentUserRole);
  const isActiveOperator = (() => {
    if (!currentUserId || !request) return false;
    const activeForRole = (role) => assignments.some(
      (a) => a.assignment_role === role && a.status !== 'HANDED_OVER' && a.assignee_user_id === currentUserId
    );
    if (request.type === 'FT' && activeForRole('FINANCIAL_OFFICER')) return true;
    if ((request.type === 'CTA' || request.type === 'FEA') && activeForRole('COORDINATOR')) return true;
    if (request.type === 'FEA' && request.fea_coordinator_user_id === currentUserId) return true;
    return false;
  })();
  const canViewAssigneeDocs = baselineCanView || isActiveOperator;

  // iter167 Issue 12 — Who can toggle the per-assignee viewer-only access?
  // Admin / CRD / Chairman, OR the request's currently-assigned Coordinator
  // (matches the backend authorization). Only on CTA / FEA.
  //   • CTA: any active COORDINATOR assignment row for current user.
  //   • FEA: request.fea_coordinator_user_id === current user (CRD delegate).
  const canToggleViewerAccess = (() => {
    if (!request || !['CTA', 'FEA'].includes(request.type)) return false;
    if (['ADMIN', 'CRD', 'CHAIRMAN'].includes(currentUserRole)) return true;
    if (currentUserRole === 'COORDINATOR') {
      const ctaCoordinator = assignments.some(
        (a) => a.assignment_role === 'COORDINATOR'
          && a.status !== 'HANDED_OVER'
          && a.assignee_user_id === currentUserId
      );
      const feaCoordinator = request.type === 'FEA'
        && request.fea_coordinator_user_id === currentUserId;
      return ctaCoordinator || feaCoordinator;
    }
    return false;
  })();

  const toggleViewerAccess = async (assignment, currentlyGranted) => {
    setTogglingViewerId(assignment.id);
    try {
      await requestsApi.toggleAssigneeViewerAccess(
        request.id,
        assignment.assignee_user_id,
        !currentlyGranted,
      );
      toast.success(
        currentlyGranted
          ? `View-only access revoked for ${assignment.assignee_name}`
          : `View-only access granted to ${assignment.assignee_name}`,
      );
      await onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to toggle viewer access');
    } finally {
      setTogglingViewerId(null);
    }
  };

  const handleRemove = async (assignment) => {
    if (!confirm(`Remove ${assignment.assignee_name} (${getRoleLabel(assignment.assignment_role, request.type)}) from this request?`)) return;
    setRemovingId(assignment.id);
    try {
      await onRemoveAssignment(assignment.id);
    } finally {
      setRemovingId(null);
    }
  };

  const isOwnRow = (assignment) =>
    currentUserId && assignment.assignee_user_id === currentUserId;

  const openDocsDialog = (assignment) => {
    const self = isOwnRow(assignment);
    if (!canViewAssigneeDocs && !self) return;
    setIsSelfView(self);
    setSelectedAssignee({
      id: assignment.assignee_user_id,
      name: assignment.assignee_name,
      role: assignment.assignment_role,
    });
    setDocDialogOpen(true);
  };

  // iter148 — per-user "pending tasks" map. An assignee is treated as having
  // open work when they're listed in any non-APPROVED, non-bundle document's
  // `status_assignees` for this request. When this is true we override the
  // assignment chip to show "Pending" (amber) even if assignment.status is DONE,
  // so the row honestly reflects "this user still owes work on a doc thread"
  // (per user request — see AssigneeDocumentsDialog "Assigned Tasks" panel).
  const pendingTaskByUserId = (() => {
    const m = {};
    (documents || []).forEach((d) => {
      if (d.is_ft_bundle) return;
      if (d.status === 'APPROVED') return;
      (d.status_assignees || []).forEach((uid) => { m[uid] = true; });
    });
    return m;
  })();

  // Most urgent expiry chip per assignee
  const expiryAlertForAssignee = (userId) => {
    const matches = docAlerts.filter((a) => a.user_id === userId && a.kind !== 'missing');
    if (matches.length === 0) return null;
    return matches.reduce((acc, cur) => {
      if (!acc) return cur;
      if (cur.expired && !acc.expired) return cur;
      if (!cur.expired && acc.expired) return acc;
      return cur.days_until < acc.days_until ? cur : acc;
    }, null);
  };

  // Aggregate missing-doc summary per assignee (staff only — Company Reps already get empty list)
  const missingForAssignee = (userId) => {
    const matches = docAlerts.filter((a) => a.user_id === userId && a.kind === 'missing');
    if (matches.length === 0) return null;
    const blocking = matches.filter((m) => m.severity === 'block');
    return {
      total: matches.length,
      blocking: blocking.length,
      types: matches.map((m) => m.doc_type),
    };
  };

  const activeAssignments = assignments.filter((a) => a.status !== 'HANDED_OVER');
  const visibleAssignments = isCompanyRep()
    ? activeAssignments.filter((a) => a.visible_to_company)
    : activeAssignments;
  const hiddenCount = isCompanyRep()
    ? activeAssignments.filter((a) => !a.visible_to_company).length
    : 0;

  // iter126 — Pending invitation rows. Hidden from Company Reps (privacy: they don't
  // need to see internal invitation pipelines). Only invitations awaiting registration
  // or awaiting admin approval are surfaced — expired ones are dropped to keep the list
  // actionable. The amber colour signals "blocking but pending" same as the missing-doc chip.
  const visiblePendingInvitations = isCompanyRep()
    ? []
    : (pendingInvitations || []).filter((inv) => inv?.status !== 'expired');

  // iter119 — Chairman opted into auto-assignment of the FT FO / CTA Coordinator to every
  // document thread. We surface a ⚡ chip on the Assignments card AND on the assignee row.
  const autoAssignActive = (request?.type === 'FT' && request?.ft_fo_thread_autoassign)
    || (request?.type === 'CTA' && request?.coordinator_thread_autoassign);
  const autoAssignRole = request?.type === 'FT' ? 'FINANCIAL_OFFICER' : (request?.type === 'CTA' ? 'COORDINATOR' : null);
  // iter131 — Chairman/Admin spark toggle (⚡) to flip the thread auto-assignment ON/OFF
  // without leaving the page. Past assignments are preserved when toggling OFF.
  const canToggleAutoAssign =
    typeof onToggleAutoAssign === 'function' &&
    (request?.type === 'CTA' || request?.type === 'FT') &&
    ((typeof isChairman === 'function' && isChairman()) || (typeof isAdmin === 'function' && isAdmin()));
  const autoAssignTargetLabel = request?.type === 'CTA' ? 'Coordinator' : 'Financial Officer';
  const [autoAssignToggling, setAutoAssignToggling] = useState(false);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="flex items-center gap-2">
            Assignments
          </CardTitle>
          {canToggleAutoAssign && (
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
              <Zap className={`w-3.5 h-3.5 ${autoAssignActive ? 'fill-amber-400 text-amber-500' : 'text-slate-400'}`} />
              <span>
                Auto-assign {autoAssignTargetLabel} to threads
              </span>
              <Switch
                checked={!!autoAssignActive}
                disabled={autoAssignToggling || request?.status === 'EXECUTED'}
                data-testid="auto-assign-toggle"
                onCheckedChange={async (next) => {
                  setAutoAssignToggling(true);
                  try {
                    await onToggleAutoAssign(next);
                  } finally {
                    setAutoAssignToggling(false);
                  }
                }}
                className={autoAssignActive ? 'data-[state=checked]:bg-amber-500' : ''}
                aria-label="Toggle thread auto-assignment"
              />
            </label>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {visibleAssignments.length === 0 && hiddenCount === 0 && visiblePendingInvitations.length === 0 ? (
          <div className="text-center py-8 text-slate-500">No active assignments</div>
        ) : (
          <div className="space-y-3">
            {visibleAssignments.map((assignment) => {
              const piIds = request.selected_pi_user_ids || [];
              const coiIds = request.selected_co_investigator_user_ids || [];
              // iter130 — FT investigators now render with the same PI / Co-I styling as CTA/FEA
              // (orange PI pill + "Principal Investigator" sub-label / blue Co-I pill + amber avatar).
              // FT assignments also carry a row-level `is_co_investigator` flag — fall back to it
              // for older FT rows created before the request-level arrays were backfilled.
              // iter166a — FEA exception: in FEA, the PI / Co-I picks at the end of feasibility
              // are a *future-CTA selection*, not a current FEA role. Within the FEA workspace
              // these consultants must remain labelled "Consultant" (matching the user's
              // mental model: "Hebah · Consultant · DONE"). PI/Co-I styling only applies in
              // CTA & FT contexts.
              // iter166b — When the row is masked (Company viewer + assignee hasn't yet
              // earned reveal), suppress ALL PI/Co-I styling: no orange-PI avatar, no
              // "Principal Investigator" sub-label, no "PI" pill. The chip simply shows
              // the generic "<Role> (pending first approval)" placeholder so the company
              // doesn't get a leaky preview of who is the PI before they've approved.
              const isMasked = !!assignment.assignee_masked;
              const isInvestigator =
                ['CTA', 'FEA', 'FT'].includes(request.type) &&
                ['PI', 'INVESTIGATOR', 'CONSULTANT'].includes(assignment.assignment_role);
              const piCoiContext = (request.type === 'CTA' || request.type === 'FT') && !isMasked;
              const isCoI = isInvestigator && piCoiContext && (
                coiIds.includes(assignment.assignee_user_id) ||
                (request.type === 'FT' && assignment.is_co_investigator === true)
              );
              const isPI = isInvestigator && piCoiContext && !isCoI && (
                piIds.includes(assignment.assignee_user_id) ||
                (request.type === 'FT' && assignment.is_co_investigator === false)
              );
              const roleLabel = isPI
                ? 'Principal Investigator'
                : isCoI
                  ? 'Co-Investigator'
                  : getRoleLabel(assignment.assignment_role, request.type);
              const avatarBg = isPI ? 'bg-amber-500' : isCoI ? 'bg-blue-500' : 'bg-blue-900';
              const expiryAlert = expiryAlertForAssignee(assignment.assignee_user_id);
              const missing = missingForAssignee(assignment.assignee_user_id);
              const self = isOwnRow(assignment);
              const rowClickable = canViewAssigneeDocs || self;
              // iter119 — ⚡ marker on the assignee whose role is the auto-assigned one
              const isAutoAssigned = autoAssignActive && assignment.assignment_role === autoAssignRole;

              return (
                <div
                  key={assignment.id}
                  className={`flex items-center gap-4 p-4 bg-slate-50 rounded-lg transition-colors ${
                    rowClickable ? 'cursor-pointer hover:bg-slate-100' : ''
                  }`}
                  data-testid={`assignment-${assignment.id}`}
                  onClick={() => rowClickable && openDocsDialog(assignment)}
                  role={rowClickable ? 'button' : undefined}
                  tabIndex={rowClickable ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (rowClickable && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      openDocsDialog(assignment);
                    }
                  }}
                >
                  <div className={`w-10 h-10 rounded-full ${avatarBg} flex items-center justify-center`}>
                    {isPI ? (
                      <Star className="w-5 h-5 text-white" />
                    ) : isCoI ? (
                      <FlaskConical className="w-5 h-5 text-white" />
                    ) : (
                      <User className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                      {/* iter162 — When a Company viewer sees an assignee who
                          has not yet approved any doc thread, the backend masks
                          the name/email. Show a friendly role-only placeholder
                          with a tooltip explaining the reveal rule. */}
                      <span data-testid={`assignment-${assignment.id}-name`}>
                        {assignment.assignee_masked
                          ? <span className="italic text-slate-500" title="Name will appear once this user approves a doc thread on your request">{getRoleLabel(assignment.assignment_role, request.type)} (pending first approval)</span>
                          : assignment.assignee_name}
                      </span>
                      {/* iter119 — ⚡ Auto-Assigned marker on the assignee whose role
                          was opted-in by the Chairman for auto-thread assignment. */}
                      {isAutoAssigned && (
                        <span
                          className="inline-flex items-center text-amber-500"
                          title="Auto-assigned to every document thread (existing & future) by Chairman"
                          data-testid={`assignment-${assignment.id}-auto-spark`}
                        >
                          <Zap className="w-3.5 h-3.5 fill-amber-400" />
                        </span>
                      )}
                      {/* iter167 Issue 12 — Per-assignee view-only access toggle.
                          Always visible on CTA / FEA Assignments tab so the
                          state is unambiguous:
                            👁  open eye   → assignee IS in viewer_user_ids
                            🚫  closed eye → assignee is NOT
                          Click to flip (Admin / CRD / Chairman or this
                          request's assigned Coordinator, including the
                          FEA CRD-delegated coordinator). Other roles see
                          the icon read-only.

                          iter167 Issue 13 — HIDE the icon entirely for the
                          assigned Coordinator's OWN row (CTA assigned
                          coordinator or FEA delegated coordinator) — they
                          have full request access by default; the eye is
                          irrelevant for them. */}
                      {['CTA', 'FEA'].includes(request.type) && (() => {
                        const isAssignedCoordinatorRow = (
                          (assignment.assignment_role === 'COORDINATOR'
                            && assignment.status !== 'HANDED_OVER')
                          || (request.type === 'FEA'
                            && request.fea_coordinator_user_id === assignment.assignee_user_id)
                        );
                        if (isAssignedCoordinatorRow) return null;
                        const granted = (request.viewer_user_ids || []).includes(assignment.assignee_user_id);
                        const Icon = granted ? Eye : EyeOff;
                        const colorClass = granted ? 'text-emerald-600' : 'text-slate-400';
                        const titleText = canToggleViewerAccess
                          ? (granted
                              ? 'View-only access ON — click to revoke (user will no longer see all docs)'
                              : 'View-only access OFF — click to grant (user will see all existing & upcoming docs)')
                          : (granted
                              ? 'Can see all existing & upcoming documents (view-only)'
                              : 'No global view-only access');
                        const isBusy = togglingViewerId === assignment.id;
                        if (!canToggleViewerAccess) {
                          return (
                            <span
                              className={`inline-flex items-center ${colorClass}`}
                              title={titleText}
                              data-testid={`assignment-${assignment.id}-viewer-eye`}
                            >
                              <Icon className="w-3.5 h-3.5" />
                            </span>
                          );
                        }
                        return (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleViewerAccess(assignment, granted); }}
                            disabled={isBusy}
                            className={`inline-flex items-center ${colorClass} hover:opacity-70 transition-opacity disabled:opacity-40`}
                            title={titleText}
                            data-testid={`assignment-${assignment.id}-viewer-toggle`}
                            data-viewer-state={granted ? 'on' : 'off'}
                          >
                            {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
                          </button>
                        );
                      })()}
                      {/* iter119 — Lock icon when the row's expanded panel is restricted.
                          Hovering explains the visibility rule. */}
                      {!rowClickable && (
                        <span
                          className="inline-flex items-center text-slate-400"
                          title="Only Admin, CRD, Chairman, Director, Executive Director, the request's assigned Coordinator (CTA/FEA), the request's assigned Financial Officer (FT), and the user themselves can view this assignee's required documents."
                          data-testid={`assignment-${assignment.id}-restricted-icon`}
                        >
                          <Lock className="w-3 h-3" />
                        </span>
                      )}
                      {assignment.assignee_user_id === request.created_by && (
                        <Badge
                          className="bg-slate-200 text-slate-700 border border-slate-300 text-[10px] px-1.5 py-0"
                          data-testid={`assignment-${assignment.id}-creator-chip`}
                        >
                          Creator
                        </Badge>
                      )}
                      {isPI && (
                        <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px] px-1.5 py-0">PI</Badge>
                      )}
                      {isCoI && (
                        <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-[10px] px-1.5 py-0">Co-I</Badge>
                      )}
                      {/* Inline expiry chip — silent if everything is fine */}
                      {expiryAlert && (
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                            expiryAlert.expired
                              ? 'bg-red-100 text-red-700 border border-red-200'
                              : 'bg-amber-100 text-amber-700 border border-amber-200'
                          }`}
                          data-testid={`assignment-${assignment.id}-expiry-chip`}
                          title={`${expiryAlert.doc_type} ${expiryAlert.expired ? 'expired' : `expires in ${expiryAlert.days_until} day${expiryAlert.days_until === 1 ? '' : 's'}`}`}
                        >
                          <AlertTriangle className="w-3 h-3" />
                          {expiryAlert.doc_type}{' '}
                          {expiryAlert.expired ? 'expired' : `expires in ${expiryAlert.days_until}d`}
                        </span>
                      )}
                      {/* Missing-doc chip — staff only (Company Reps receive empty docAlerts) */}
                      {missing && (
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                            missing.blocking > 0
                              ? 'bg-red-100 text-red-700 border border-red-200'
                              : 'bg-amber-100 text-amber-700 border border-amber-200'
                          }`}
                          data-testid={`assignment-${assignment.id}-missing-chip`}
                          title={missing.types.join(', ')}
                        >
                          <AlertTriangle className="w-3 h-3" />
                          {missing.blocking > 0
                            ? `Blocked: ${missing.total} doc${missing.total === 1 ? '' : 's'}`
                            : `Missing ${missing.total} doc${missing.total === 1 ? '' : 's'}`}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{roleLabel}</p>
                  </div>
                  <Badge
                    className={(() => {
                      // iter148 — assignee chip reflects open doc-thread tasks: if the user
                      // still owes review work on at least one non-APPROVED doc, render the
                      // amber "Pending" chip even if assignment.status === 'DONE'. The
                      // SKIPPED_BY_CHAIRMAN/PENDING/IN_PROGRESS rows stay amber.
                      if (assignment.status === 'DONE' && !pendingTaskByUserId[assignment.assignee_user_id]) {
                        return 'bg-emerald-100 text-emerald-700';
                      }
                      return 'bg-amber-100 text-amber-700';
                    })()}
                    data-testid={`assignment-status-${assignment.id}`}
                  >
                    {(() => {
                      // iter127 — display "Skipped by Coordinator/Chairman" instead of the
                      // raw SKIPPED_BY_CHAIRMAN status name so the UI reflects who actually
                      // performed the skip (Coordinator can now skip in iter126).
                      if (assignment.status === 'SKIPPED_BY_CHAIRMAN') {
                        const skipperRole = request?.chairman_skipped_approvals?.skipped_by_role;
                        return skipperRole === 'COORDINATOR' ? 'Skipped by Coordinator' : 'Skipped by Chairman';
                      }
                      // iter148 — even DONE rows fall back to "Pending" when there is
                      // outstanding doc-thread review work for this assignee.
                      if (assignment.status === 'DONE' && pendingTaskByUserId[assignment.assignee_user_id]) {
                        return 'Pending';
                      }
                      return assignment.status;
                    })()}
                  </Badge>
                  {assignment.due_date && (
                    <span className="text-xs text-slate-400">Due: {formatDate(assignment.due_date)}</span>
                  )}
                  {/* iter163j — Per-row "DM this person" button. Hidden when:
                      - viewer can't use DMs (Company / Company Rep)
                      - assignee name is masked (no real user to message)
                      - assignee is the viewer themselves */}
                  {dmEligible
                    && !assignment.assignee_masked
                    && assignment.assignee_user_id
                    && assignment.assignee_user_id !== currentUserId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                      onClick={(e) => {
                        e.stopPropagation();
                        startDMWith({
                          user_id: assignment.assignee_user_id,
                          peer_name: assignment.assignee_name,
                          embed: {
                            kind: 'request',
                            ref_no: request.ref_no,
                            label: `${request.ref_no} — ${request.title || ''}`,
                          },
                        });
                      }}
                      title="Private message — only you and them can read"
                      data-testid={`assignment-${assignment.id}-dm-btn`}
                    >
                      <Mail size={14} />
                    </Button>
                  )}
                  {isAdmin && isAdmin() && onRemoveAssignment && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(assignment);
                      }}
                      disabled={removingId === assignment.id}
                      data-testid={`remove-assignment-${assignment.id}`}
                    >
                      {removingId === assignment.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
              );
            })}

            {isCompanyRep() && hiddenCount > 0 && (
              <div className="p-4 bg-slate-100 rounded-lg text-center text-slate-600">
                <Users className="w-5 h-5 inline mr-2" />
                {hiddenCount} additional team member(s) assigned
              </div>
            )}

            {/* iter126 — Pending invitations (PI / Investigator / Consultant invites that
                haven't completed registration yet). Surfaced inline so Coordinators / CRD /
                Chairman can see who is blocking the workflow without leaving the page. */}
            {visiblePendingInvitations.map((inv, idx) => {
              const cta = inv.cta_role;
              const ctaLabel = cta === 'PI'
                ? 'Principal Investigator'
                : (cta === 'CO_INVESTIGATOR' || cta === 'COI' || cta === 'CO-I')
                  ? 'Co-Investigator'
                  : request?.type === 'FEA' ? 'Investigator' : 'Investigator';
              const isAwaitingAdmin = inv.status === 'registered_pending_admin_approval';
              return (
                <div
                  key={`pending-invite-${inv.email}-${idx}`}
                  className="flex items-center gap-4 p-4 bg-amber-50/60 border border-dashed border-amber-300 rounded-lg"
                  data-testid={`pending-invitation-${inv.email}`}
                >
                  <div className="w-10 h-10 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-amber-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                      <span className="truncate">{inv.name || inv.email}</span>
                      {cta === 'PI' && (
                        <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px] px-1.5 py-0">PI</Badge>
                      )}
                      {(cta === 'CO_INVESTIGATOR' || cta === 'COI' || cta === 'CO-I') && (
                        <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-[10px] px-1.5 py-0">Co-I</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <span>{ctaLabel}</span>
                      {inv.name && (
                        <span className="text-slate-400">· {inv.email}</span>
                      )}
                    </p>
                  </div>
                  <Badge
                    className="bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-1"
                    data-testid={`pending-invitation-${inv.email}-badge`}
                  >
                    <Clock className="w-3 h-3" />
                    {isAwaitingAdmin ? 'Awaiting admin approval' : 'Awaiting registration'}
                  </Badge>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {(canViewAssigneeDocs || selectedAssignee) && (
        <AssigneeDocumentsDialog
          requestId={request.id}
          assignee={selectedAssignee}
          open={docDialogOpen}
          viewerIsSelf={isSelfView}
          onOpenChange={(o) => { setDocDialogOpen(o); if (!o) { setSelectedAssignee(null); setIsSelfView(false); } }}
        />
      )}
    </Card>
  );
}

export default AssignmentsTab;
